import { IM_NOTIFICATION_PLATFORMS } from '../../../shared/imNotifications';
import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { scheduledTaskService } from '../../services/scheduledTask';
import { coworkService } from '../../services/cowork';
import { i18nService, type LanguageType } from '../../services/i18n';
import type { ScheduledTask, Schedule, ScheduledTaskInput, NotifyPlatform } from '../../types/scheduledTask';
import type { SandboxProfile, SandboxImage } from '../../types/cowork';
import ExecutionEnvPopover from '../cowork/ExecutionEnvPopover';
import { buildCloudEnvOptions, findCloudEnvOption } from '../../utils/sandboxEnv';

interface TaskFormProps {
  mode: 'create' | 'edit';
  task?: ScheduledTask;
  onCancel: () => void;
  onSaved: () => void;
}

type ScheduleMode = 'once' | 'daily' | 'weekly' | 'monthly';

const NOTIFY_PLATFORMS = IM_NOTIFICATION_PLATFORMS;
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const; // 0=Sunday
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

interface LocalizedDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  min: string;
  className: string;
  wrapperClassName?: string;
  ariaLabel: string;
}

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDateLocale = (language: LanguageType): string => {
  if (language === 'zh') return 'zh-CN';
  if (language === 'zh-TW') return 'zh-TW';
  return 'en-US';
};

const LocalizedDatePicker: React.FC<LocalizedDatePickerProps> = ({
  value,
  onChange,
  min,
  className,
  wrapperClassName = '',
  ariaLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const initialDate = parseDateKey(value) ?? parseDateKey(min) ?? new Date();
    return new Date(initialDate.getFullYear(), initialDate.getMonth(), 1);
  });
  const language = i18nService.getLanguage();
  const locale = getDateLocale(language);
  const [year, month, day] = value.split('-');
  const displayValue = value
    ? language === 'en'
      ? `${month}/${day}/${year}`
      : `${year}/${month}/${day}`
    : i18nService.t('scheduledTasksFormDatePlaceholder');

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const viewYear = viewMonth.getFullYear();
  const viewMonthIndex = viewMonth.getMonth();
  const firstWeekday = new Date(viewYear, viewMonthIndex, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonthIndex + 1, 0).getDate();
  const calendarCells: Array<number | null> = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  const monthTitle = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
  }).format(viewMonth);
  const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const dateLabelFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'long' });
  const weekdayLabels = Array.from({ length: 7 }, (_, index) => (
    weekdayFormatter.format(new Date(2024, 0, 7 + index))
  ));
  const todayKey = formatDateKey(new Date());
  const previousMonthLastDay = formatDateKey(new Date(viewYear, viewMonthIndex, 0));
  const canGoPrevious = previousMonthLastDay >= min;

  const toggleCalendar = () => {
    if (!open) {
      const focusedDate = parseDateKey(value) ?? parseDateKey(min) ?? new Date();
      setViewMonth(new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1));
    }
    setOpen((current) => !current);
  };

  return (
    <div ref={containerRef} className={`relative ${wrapperClassName}`}>
      <button
        type="button"
        onClick={toggleCalendar}
        className={`${className} flex items-center justify-between text-left cursor-pointer`}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={value ? '' : 'dark:text-claude-darkTextSecondary text-claude-textSecondary'}>
          {displayValue}
        </span>
        <svg className="w-4 h-4 shrink-0 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 2v3m8-3v3M3 9h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" />
        </svg>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={i18nService.t('scheduledTasksFormCalendarLabel')}
          className="absolute left-0 top-full z-30 mt-1 w-72 rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white p-3 shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewYear, viewMonthIndex - 1, 1))}
              disabled={!canGoPrevious}
              aria-label={i18nService.t('scheduledTasksFormCalendarPreviousMonth')}
              title={i18nService.t('scheduledTasksFormCalendarPreviousMonth')}
              className="flex h-8 w-8 items-center justify-center rounded-lg dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover disabled:cursor-not-allowed disabled:opacity-30"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="text-sm font-semibold dark:text-claude-darkText text-claude-text">
              {monthTitle}
            </div>
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewYear, viewMonthIndex + 1, 1))}
              aria-label={i18nService.t('scheduledTasksFormCalendarNextMonth')}
              title={i18nService.t('scheduledTasksFormCalendarNextMonth')}
              className="flex h-8 w-8 items-center justify-center rounded-lg dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {weekdayLabels.map((label, index) => (
              <div key={`${label}-${index}`} className="py-1 text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {label}
              </div>
            ))}
            {calendarCells.map((calendarDay, index) => {
              if (calendarDay === null) return <div key={`empty-${index}`} />;
              const date = new Date(viewYear, viewMonthIndex, calendarDay);
              const dateKey = formatDateKey(date);
              const disabled = dateKey < min;
              const selected = dateKey === value;
              const today = dateKey === todayKey;
              return (
                <button
                  key={dateKey}
                  type="button"
                  onClick={() => {
                    onChange(dateKey);
                    setOpen(false);
                  }}
                  disabled={disabled}
                  aria-label={dateLabelFormatter.format(date)}
                  aria-current={today ? 'date' : undefined}
                  aria-pressed={selected}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors ${
                    selected
                      ? 'bg-claude-accent text-white'
                      : disabled
                        ? 'cursor-not-allowed opacity-30 dark:text-claude-darkTextSecondary text-claude-textSecondary'
                        : `dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover ${
                            today ? 'ring-1 ring-claude-accent' : ''
                          }`
                  }`}
                >
                  {calendarDay}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Parse existing schedule into UI state
function parseScheduleToUI(schedule: Schedule): {
  mode: ScheduleMode;
  date: string;
  time: string;
  weekday: number;
  monthDay: number;
} {
  const defaults = { mode: 'once' as ScheduleMode, date: '', time: '09:00', weekday: 1, monthDay: 1 };

  if (schedule.type === 'at') {
    const dt = schedule.datetime ?? '';
    // datetime-local format: "YYYY-MM-DDTHH:MM"
    if (dt.includes('T')) {
      return { ...defaults, mode: 'once', date: dt.slice(0, 10), time: dt.slice(11, 16) };
    }
    return { ...defaults, mode: 'once', date: dt.slice(0, 10) };
  }

  if (schedule.type === 'cron' && schedule.expression) {
    const parts = schedule.expression.trim().split(/\s+/);
    if (parts.length >= 5) {
      const [min, hour, dom, , dow] = parts;
      const timeStr = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;

      if (dow !== '*' && dom === '*') {
        // Weekly: M H * * DOW
        return { ...defaults, mode: 'weekly', time: timeStr, weekday: parseInt(dow) || 0 };
      }
      if (dom !== '*' && dow === '*') {
        // Monthly: M H DOM * *
        return { ...defaults, mode: 'monthly', time: timeStr, monthDay: parseInt(dom) || 1 };
      }
      // Daily: M H * * *
      return { ...defaults, mode: 'daily', time: timeStr };
    }
  }

  // Fallback for interval type - treat as daily
  if (schedule.type === 'interval') {
    return { ...defaults, mode: 'daily' };
  }

  return defaults;
}

const TaskForm: React.FC<TaskFormProps> = ({ mode, task, onCancel, onSaved }) => {
  // Parse existing schedule for edit mode
  const parsed = task ? parseScheduleToUI(task.schedule) : null;

  // Form state
  const [name, setName] = useState(task?.name ?? '');
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>(parsed?.mode ?? 'once');
  const [scheduleDate, setScheduleDate] = useState(parsed?.date ?? '');
  const [scheduleHour, setScheduleHour] = useState((parsed?.time ?? '09:00').slice(0, 2));
  const [scheduleMinute, setScheduleMinute] = useState((parsed?.time ?? '09:00').slice(3, 5));
  const [weekday, setWeekday] = useState(parsed?.weekday ?? 1);
  const [monthDay, setMonthDay] = useState(parsed?.monthDay ?? 1);
  const [prompt, setPrompt] = useState(task?.prompt ?? '');
  const [expiresAt, setExpiresAt] = useState(task?.expiresAt ?? '');
  const [notifyPlatforms, setNotifyPlatforms] = useState<NotifyPlatform[]>(task?.notifyPlatforms ?? []);
  const [sandboxProfileId, setSandboxProfileId] = useState<string>(task?.sandboxProfileId ?? '');
  const [sandboxImageId, setSandboxImageId] = useState<string>(task?.sandboxImageId ?? '');
  const [modelId, setModelId] = useState<string>(task?.modelId ?? '');
  const [sandboxProfiles, setSandboxProfiles] = useState<SandboxProfile[]>([]);
  const [sandboxImages, setSandboxImages] = useState<SandboxImage[]>([]);
  const availableModels = useSelector((state: RootState) => state.model.availableModels);
  const [envMenuOpen, setEnvMenuOpen] = useState(false);
  const envButtonRef = useRef<HTMLButtonElement>(null);
  const [notifyDropdownOpen, setNotifyDropdownOpen] = useState(false);
  const notifyDropdownRef = useRef<HTMLDivElement>(null);
  const [hourDropdownOpen, setHourDropdownOpen] = useState(false);
  const [minuteDropdownOpen, setMinuteDropdownOpen] = useState(false);
  const hourDropdownRef = useRef<HTMLDivElement>(null);
  const minuteDropdownRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 运行环境与模板成对加载：模板决定运行环境，单独存一个都会建出跑不起来的沙箱。
  useEffect(() => {
    Promise.all([
      coworkService.getSandboxProfiles(),
      coworkService.getSandboxImageCatalog(),
    ]).then(([profiles, images]) => {
      setSandboxProfiles(profiles);
      setSandboxImages(images);

      // 编辑既有任务时按它存的值回填；命中不了（模板被删/被停用）就留空，
      // 让用户重新选，而不是悄悄换成另一个环境。
      const options = buildCloudEnvOptions(profiles, images);
      const matched = findCloudEnvOption(options, task?.sandboxProfileId, task?.sandboxImageId);
      const chosen = matched ?? (mode === 'create' ? options[0] ?? null : null);
      setSandboxProfileId(chosen?.profileId ?? '');
      setSandboxImageId(chosen?.imageId ?? '');
    }).catch(() => {});
  }, [mode, task?.sandboxImageId, task?.sandboxProfileId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (notifyDropdownRef.current && !notifyDropdownRef.current.contains(target)) {
        setNotifyDropdownOpen(false);
      }
      if (hourDropdownRef.current && !hourDropdownRef.current.contains(target)) {
        setHourDropdownOpen(false);
      }
      if (minuteDropdownRef.current && !minuteDropdownRef.current.contains(target)) {
        setMinuteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildSchedule = (): Schedule => {
    const hour = Number(scheduleHour);
    const min = Number(scheduleMinute);
    switch (scheduleMode) {
      case 'once':
        return { type: 'at', datetime: `${scheduleDate}T${scheduleHour}:${scheduleMinute}` };
      case 'daily':
        return { type: 'cron', expression: `${min} ${hour} * * *` };
      case 'weekly':
        return { type: 'cron', expression: `${min} ${hour} * * ${weekday}` };
      case 'monthly':
        return { type: 'cron', expression: `${min} ${hour} ${monthDay} * *` };
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = i18nService.t('scheduledTasksFormValidationNameRequired');
    if (!prompt.trim()) newErrors.prompt = i18nService.t('scheduledTasksFormValidationPromptRequired');
    // 编辑老任务时，它绑定的模板可能已被停用或删除，回填会落空。这里必须拦住：
    // 否则只改一下时间就把执行环境一起清空了，任务下次跑在默认环境上。
    if (!activeCloudEnv) newErrors.executionEnv = i18nService.t('scheduledTasksFormValidationEnvRequired');
    if (scheduleMode === 'once') {
      if (!scheduleDate) {
        newErrors.schedule = i18nService.t('scheduledTasksFormValidationDatetimeFuture');
      } else if (new Date(`${scheduleDate}T${scheduleHour}:${scheduleMinute}`).getTime() <= Date.now()) {
        newErrors.schedule = i18nService.t('scheduledTasksFormValidationDatetimeFuture');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const input: ScheduledTaskInput = {
        name: name.trim(),
        description: '',
        schedule: buildSchedule(),
        prompt: prompt.trim(),
        workingDirectory: '',
        systemPrompt: '',
        executionMode: 'sandbox',
        expiresAt: expiresAt || null,
        notifyPlatforms,
        sandboxProfileId: sandboxProfileId || null,
        sandboxImageId: sandboxImageId || null,
        modelId: modelId || null,
        enabled: task?.enabled ?? true,
      };
      if (mode === 'create') {
        await scheduledTaskService.createTask(input);
      } else if (task) {
        await scheduledTaskService.updateTaskById(task.id, input);
      }
      onSaved();
    } catch {
      // Error handled by service
    } finally {
      setSubmitting(false);
    }
  };

  const weekdayKeys: Record<number, string> = {
    0: 'scheduledTasksFormWeekSun',
    1: 'scheduledTasksFormWeekMon',
    2: 'scheduledTasksFormWeekTue',
    3: 'scheduledTasksFormWeekWed',
    4: 'scheduledTasksFormWeekThu',
    5: 'scheduledTasksFormWeekFri',
    6: 'scheduledTasksFormWeekSat',
  };

  const inputClass = 'w-full rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white px-3 py-2 text-sm dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent/50';
  const labelClass = 'block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1';
  const errorClass = 'text-xs text-red-500 mt-1';
  const cloudEnvOptions = buildCloudEnvOptions(sandboxProfiles, sandboxImages);
  const activeCloudEnv = findCloudEnvOption(cloudEnvOptions, sandboxProfileId, sandboxImageId);

  const scheduleModes: ScheduleMode[] = ['once', 'daily', 'weekly', 'monthly'];

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
        {mode === 'create' ? i18nService.t('scheduledTasksFormCreate') : i18nService.t('scheduledTasksFormUpdate')}
      </h2>

      {/* Name */}
      <div>
        <label className={labelClass}>{i18nService.t('scheduledTasksFormName')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
          placeholder={i18nService.t('scheduledTasksFormNamePlaceholder')}
        />
        {errors.name && <p className={errorClass}>{errors.name}</p>}
      </div>

      {/* Prompt */}
      <div>
        <label className={labelClass}>{i18nService.t('scheduledTasksPrompt')}</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className={inputClass + ' h-28 resize-none'}
          placeholder={i18nService.t('scheduledTasksFormPromptPlaceholder')}
        />
        {errors.prompt && <p className={errorClass}>{errors.prompt}</p>}
      </div>

      {/* 执行环境：与对话框用同一个选择器。列的是沙箱模板，运行环境跟着模板走，
          不再让人先挑一次「沙箱环境」——那是部署侧概念，对使用者没有意义。
          定时任务只在云端跑，所以不给「桌面客户端」那一组。 */}
      <div>
        <label className={labelClass}>{i18nService.t('executionEnvLabel')}</label>
        <div className="relative">
          <button
            ref={envButtonRef}
            type="button"
            onClick={() => setEnvMenuOpen(!envMenuOpen)}
            className={`${inputClass} flex items-center justify-between gap-2 text-left`}
          >
            <span className={`truncate ${activeCloudEnv ? '' : 'dark:text-claude-darkTextSecondary text-claude-textSecondary'}`}>
              {activeCloudEnv?.label ?? i18nService.t('executionEnvUnset')}
            </span>
            <svg className="h-4 w-4 flex-shrink-0 dark:text-claude-darkTextSecondary text-claude-textSecondary" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
          <ExecutionEnvPopover
            isOpen={envMenuOpen}
            onClose={() => setEnvMenuOpen(false)}
            anchorRef={envButtonRef as React.RefObject<HTMLElement>}
            profiles={sandboxProfiles}
            images={sandboxImages}
            selectedProfileId={sandboxProfileId || null}
            selectedImageId={sandboxImageId || null}
            onSelectCloud={(profileId, imageId) => {
              setSandboxProfileId(profileId ?? '');
              setSandboxImageId(imageId ?? '');
            }}
            placement="down"
          />
        </div>
        {errors.executionEnv && <p className={errorClass}>{errors.executionEnv}</p>}
      </div>

      {/* Model */}
      <div>
        <label className={labelClass}>{i18nService.t('scheduledTasksFormModel')}</label>
        <select
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          className={inputClass}
        >
          <option value="">{i18nService.t('scheduledTasksFormModelDefault')}</option>
          {availableModels.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}{m.provider ? ` · ${m.provider}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Schedule */}
      <div>
        <label className={labelClass}>{i18nService.t('scheduledTasksFormScheduleType')}</label>
        <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
          {/* Schedule Mode */}
          <select
            value={scheduleMode}
            onChange={(e) => setScheduleMode(e.target.value as ScheduleMode)}
            className={inputClass}
          >
            {scheduleModes.map((m) => (
              <option key={m} value={m}>
                {i18nService.t(`scheduledTasksFormScheduleMode${m.charAt(0).toUpperCase() + m.slice(1)}`)}
              </option>
            ))}
          </select>

          {/* Date / Weekday / MonthDay */}
          {scheduleMode === 'once' ? (
            <LocalizedDatePicker
              value={scheduleDate}
              onChange={setScheduleDate}
              className={inputClass}
              min={new Date().toISOString().slice(0, 10)}
              ariaLabel={i18nService.t('scheduledTasksFormDatetime')}
            />
          ) : scheduleMode === 'weekly' ? (
            <select
              value={weekday}
              onChange={(e) => setWeekday(parseInt(e.target.value))}
              className={inputClass}
            >
              {WEEKDAYS.map((d) => (
                <option key={d} value={d}>
                  {i18nService.t(weekdayKeys[d])}
                </option>
              ))}
            </select>
          ) : scheduleMode === 'monthly' ? (
            <select
              value={monthDay}
              onChange={(e) => setMonthDay(parseInt(e.target.value))}
              className={inputClass}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}{i18nService.t('scheduledTasksFormMonthDaySuffix')}
                </option>
              ))}
            </select>
          ) : <div />}

          {/* Hour : Minute */}
          <div className="flex items-center gap-1">
            {/* Hour dropdown */}
            <div className="relative" ref={hourDropdownRef}>
              <button
                type="button"
                onClick={() => { setHourDropdownOpen(!hourDropdownOpen); setMinuteDropdownOpen(false); }}
                className={inputClass + ' !w-16 !px-2 text-center cursor-pointer'}
              >
                {scheduleHour}
              </button>
              {hourDropdownOpen && (
                <div className="absolute z-20 mt-1 w-16 max-h-48 overflow-y-auto rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white shadow-lg py-1">
                  {HOURS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => { setScheduleHour(h); setHourDropdownOpen(false); }}
                      className={`w-full px-2 py-1.5 text-sm text-center transition-colors ${
                        scheduleHour === h
                          ? 'bg-claude-accent/10 text-claude-accent font-medium'
                          : 'dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
                      }`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="dark:text-claude-darkText text-claude-text font-medium">:</span>
            {/* Minute dropdown */}
            <div className="relative" ref={minuteDropdownRef}>
              <button
                type="button"
                onClick={() => { setMinuteDropdownOpen(!minuteDropdownOpen); setHourDropdownOpen(false); }}
                className={inputClass + ' !w-16 !px-2 text-center cursor-pointer'}
              >
                {scheduleMinute}
              </button>
              {minuteDropdownOpen && (
                <div className="absolute z-20 mt-1 w-16 max-h-48 overflow-y-auto rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white shadow-lg py-1">
                  {MINUTES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setScheduleMinute(m); setMinuteDropdownOpen(false); }}
                      className={`w-full px-2 py-1.5 text-sm text-center transition-colors ${
                        scheduleMinute === m
                          ? 'bg-claude-accent/10 text-claude-accent font-medium'
                          : 'dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {errors.schedule && <p className={errorClass}>{errors.schedule}</p>}
      </div>

      {/* Expires At */}
      <div>
        <label className={labelClass}>
          {i18nService.t('scheduledTasksFormExpiresAt')}
          <span className="text-xs font-normal dark:text-claude-darkTextSecondary text-claude-textSecondary ml-1">
            {i18nService.t('scheduledTasksFormOptional')}
          </span>
        </label>
        <div className="flex items-center gap-2">
          <LocalizedDatePicker
            value={expiresAt}
            onChange={setExpiresAt}
            className={inputClass}
            wrapperClassName="flex-1"
            min={new Date().toISOString().slice(0, 10)}
            ariaLabel={i18nService.t('scheduledTasksFormExpiresAt')}
          />
          {expiresAt && (
            <button
              type="button"
              onClick={() => setExpiresAt('')}
              className="px-3 py-2 text-sm rounded-lg border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
            >
              {i18nService.t('scheduledTasksFormExpiresAtClear')}
            </button>
          )}
        </div>
      </div>

      {/* Notification */}
      <div>
        <label className={labelClass}>
          {i18nService.t('scheduledTasksFormNotify')}
          <span className="text-xs font-normal dark:text-claude-darkTextSecondary text-claude-textSecondary ml-1">
            {i18nService.t('scheduledTasksFormOptional')}
          </span>
        </label>
        <div className="relative" ref={notifyDropdownRef}>
          <button
            type="button"
            onClick={() => setNotifyDropdownOpen(!notifyDropdownOpen)}
            className={inputClass + ' flex items-center justify-between cursor-pointer text-left'}
          >
            <span className={notifyPlatforms.length === 0 ? 'dark:text-claude-darkTextSecondary text-claude-textSecondary' : ''}>
              {notifyPlatforms.length === 0
                ? i18nService.t('scheduledTasksFormNotifyNone')
                : notifyPlatforms.map((p) =>
                    i18nService.t(`scheduledTasksFormNotify${p.charAt(0).toUpperCase() + p.slice(1)}`)
                  ).join(', ')}
            </span>
            <svg className={`w-4 h-4 ml-2 transition-transform ${notifyDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {notifyDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white shadow-lg py-1">
              {NOTIFY_PLATFORMS.map((platform) => {
                const checked = notifyPlatforms.includes(platform);
                return (
                  <label
                    key={platform}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setNotifyPlatforms(
                          checked
                            ? notifyPlatforms.filter((p) => p !== platform)
                            : [...notifyPlatforms, platform]
                        );
                      }}
                      className="text-claude-accent focus:ring-claude-accent rounded"
                    />
                    <span className="text-sm dark:text-claude-darkText text-claude-text">
                      {i18nService.t(`scheduledTasksFormNotify${platform.charAt(0).toUpperCase() + platform.slice(1)}`)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {notifyPlatforms.length > 0 && <p className="text-xs leading-relaxed text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('scheduledTasksNotifyTargetHint')}</p>}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
        >
          {i18nService.t('cancel')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium bg-claude-accent text-white rounded-lg hover:bg-claude-accentHover transition-colors disabled:opacity-50"
        >
          {submitting
            ? i18nService.t('saving')
            : mode === 'create'
              ? i18nService.t('scheduledTasksFormCreate')
              : i18nService.t('scheduledTasksFormUpdate')}
        </button>
      </div>
    </div>
  );
};

export default TaskForm;
